import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

/* =========================================================
   GOOGLE MAPS CONSTANTS + LOADER
========================================================= */

const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const SELECTED_LOCATION_ZOOM = 16;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;

  return document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]'
  );
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");

  if (errorText.includes("RefererNotAllowedMapError")) {
    return (
      "This website is not authorized for the Google Maps API key. " +
      "Add this frontend domain under the API key website restrictions."
    );
  }

  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }

  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }

  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }

  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }

  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("Google Maps can only be loaded in a browser.")
    );
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (!apiKey) {
    return Promise.reject(
      new Error(
        "Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file."
      )
    );
  }

  if (googleMapsLoadingPromise) {
    return googleMapsLoadingPromise;
  }

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;

    const resolveOnce = () => {
      if (settled) return;
      if (!window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;

      reject(
        error instanceof Error
          ? error
          : new Error("Google Maps JavaScript API failed to load.")
      );
    };

    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }

      rejectOnce(
        new Error(
          "Google Maps script loaded, but the Maps API is unavailable."
        )
      );
    };

    const handleScriptError = () => {
      rejectOnce(
        new Error("Google Maps JavaScript API network request failed.")
      );
    };

    const previousAuthFailure = window.gm_authFailure;

    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (callbackError) {
          console.error(
            "Previous Google Maps authentication callback failed:",
            callbackError
          );
        }
      }

      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };

    const existingScript = findExistingGoogleMapsScript();

    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, {
        once: true,
      });

      existingScript.addEventListener("error", handleScriptError, {
        once: true,
      });

      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);

      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;

    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places",
      v: "weekly",
    });

    script.src =
      "https://maps.googleapis.com/maps/api/js?" +
      queryParameters.toString();

    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");

    script.onload = handleScriptLoad;

    script.onerror = () => {
      script.remove();
      handleScriptError();
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
}

/* =========================================================
   COMMON HELPERS
========================================================= */

const initialRouteForm = {
  route_id: "",
  r_name: "",
  r_from: "",
  r_to: "",
  r_from_lat: "",
  r_from_lng: "",
  r_to_lat: "",
  r_to_lng: "",
};

const initialCheckpoint = {
  checkpoints_id: "",
  checkpoint_name: "",
  lat: "",
  lng: "",
  nfc_uid: "",
  nfc_token: "",
  nfc_is_active: true,
  verification_method: "NFC_OR_QR_GPS",
};

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function normalizeNfcUid(value = "") {
  return String(value)
    .trim()
    .replace(/:/g, "")
    .replace(/-/g, "")
    .toUpperCase();
}

function readNdefTextRecord(record) {
  try {
    if (!record?.data) return "";

    const decoder = new TextDecoder(record.encoding || "utf-8");
    return decoder.decode(record.data).trim();
  } catch (error) {
    console.error("Unable to decode NFC record:", error);
    return "";
  }
}

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();

  if (
    provider === "google" ||
    provider === "googlemaps" ||
    provider === "google_maps"
  ) {
    return "google";
  }

  return "openstreetmap";
}

function createCustomIcon({
  label,
  bg = "#2563eb",
  border = "#ffffff",
  textColor = "#ffffff",
  isLogo = false,
  logoUrl = "",
}) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${bg};
          border: 3px solid ${border};
          box-shadow: 0 4px 14px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width: 28px; height: 28px; object-fit: contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${bg};
        border: 2px solid ${border};
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          font-family: Arial, sans-serif;
          line-height: 1;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createCustomIcon({
  label: "F",
  bg: "#16a34a",
  isLogo: true,
  logoUrl: logo,
});
const toIcon = createCustomIcon({
  label: "T",
  bg: "#dc2626",
  isLogo: true,
  logoUrl: logo,
});

function checkpointIcon(index) {
  return createCustomIcon({
    label: String(index + 1),
    bg: "#2563eb",
  });
}

/* =========================================================
   GOOGLE MAPS CUSTOM ICONS (canvas – logo + color)
========================================================= */

/**
 * Creates a circular Google Maps icon with the company logo
 * on a colored background (used for From / To).
 */
function createGoogleLogoIcon(bgColor, logoUrl, size = 44) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    // Outer colored circle
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.fillStyle = bgColor;
    ctx.fill();

    // White border
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();

    // Soft shadow ring
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1.5, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 1;
    ctx.stroke();

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;

    img.onload = () => {
      const logoSize = Math.round(size * 0.62);
      const offset = (size - logoSize) / 2;
      ctx.drawImage(img, offset, offset, logoSize, logoSize);
      resolve({
        url: canvas.toDataURL("image/png"),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size),
      });
    };

    img.onerror = () => {
      // Fallback – solid circle if logo fails to load
      resolve({
        url: canvas.toDataURL("image/png"),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size),
      });
    };
  });
}

/**
 * Creates a numbered blue location pin for checkpoints.
 */
function createGoogleCheckpointIcon(number, size = 36) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size + 8; // extra space for the pin tip feel
  const ctx = canvas.getContext("2d");

  const r = size / 2 - 2;
  const cx = size / 2;
  const cy = size / 2 - 2;

  // Circle
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "#2563eb";
  ctx.fill();

  // White border
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();

  // Number
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${Math.round(size * 0.42)}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(number), cx, cy);

  return {
    url: canvas.toDataURL("image/png"),
    scaledSize: new window.google.maps.Size(size, size + 8),
    anchor: new window.google.maps.Point(size / 2, size + 6),
  };
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function SentinelAddRoute() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [form, setForm] = useState(initialRouteForm);
  const [checkpoints, setCheckpoints] = useState([]);
  const [activeTarget, setActiveTarget] = useState(null);
  const [scanningCheckpointIndex, setScanningCheckpointIndex] = useState(null);

  const activeTargetRef = useRef(null);
  const nfcAbortControllerRef = useRef(null);

  /* -------------------------------------------------------
     OPENSTREETMAP (LEAFLET) REFS
  ------------------------------------------------------- */
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const checkpointMarkersRef = useRef([]);
  const leafletClickHandlerRef = useRef(null);

  /* -------------------------------------------------------
     GOOGLE MAP REFS
  ------------------------------------------------------- */
  const googleMapElementRef = useRef(null);
  const googleMapInstanceRef = useRef(null);
  const googleGeocoderRef = useRef(null);
  const googleFromMarkerRef = useRef(null);
  const googleToMarkerRef = useRef(null);
  const googleCheckpointMarkersRef = useRef([]);
  const googleClickListenerRef = useRef(null);

  const hasAutoFitRef = useRef(false);
  const errorShownRef = useRef(false);

  /* -------------------------------------------------------
     TENANT MAP PROVIDER
  ------------------------------------------------------- */
  const mapProvider = useMemo(
    () => normalizeMapProvider(dashboardData.user_map),
    [dashboardData.user_map]
  );

  const isGoogleMap = mapProvider === "google";

  useEffect(() => {
    activeTargetRef.current = activeTarget;
  }, [activeTarget]);

  const setLatLngOnActiveTarget = useCallback(
    (lat, lng, shouldCenter = false, label = "") => {
      const target = activeTargetRef.current;

      if (!target) {
        toast.error(
          "Please click Pick on Map for From, To, or Checkpoint first."
        );
        return;
      }

      const lat7 = Number(lat).toFixed(7);
      const lng7 = Number(lng).toFixed(7);

      if (target === "from") {
        setForm((prev) => ({
          ...prev,
          r_from_lat: lat7,
          r_from_lng: lng7,
          r_from: label || prev.r_from,
        }));
        toast.success(`From location set: ${label || "Selected location"}`);
      } else if (target === "to") {
        setForm((prev) => ({
          ...prev,
          r_to_lat: lat7,
          r_to_lng: lng7,
          r_to: label || prev.r_to,
        }));
        toast.success(`To location set: ${label || "Selected location"}`);
      } else if (typeof target === "object" && target.type === "checkpoint") {
        setCheckpoints((prev) =>
          prev.map((checkpoint, index) =>
            index === target.index
              ? {
                  ...checkpoint,
                  lat: lat7,
                  lng: lng7,
                }
              : checkpoint
          )
        );
        toast.success(`Checkpoint ${target.index + 1} location set`);
      }

      if (shouldCenter) {
        if (!isGoogleMap && mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 16);
        }

        if (isGoogleMap && googleMapInstanceRef.current) {
          googleMapInstanceRef.current.panTo({
            lat: Number(lat),
            lng: Number(lng),
          });
          googleMapInstanceRef.current.setZoom(SELECTED_LOCATION_ZOOM);
        }
      }
    },
    [isGoogleMap]
  );

  useEffect(() => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate, editId]);

  /* =======================================================
     DESTROY OPENSTREETMAP
  ======================================================= */
  const destroyLeafletMap = useCallback(() => {
    const map = mapInstanceRef.current;

    if (map && leafletClickHandlerRef.current) {
      map.off("click", leafletClickHandlerRef.current);
    }

    if (map) {
      map.remove();
    }

    mapInstanceRef.current = null;
    geocoderRef.current = null;
    fromMarkerRef.current = null;
    toMarkerRef.current = null;
    checkpointMarkersRef.current = [];
    leafletClickHandlerRef.current = null;
    hasAutoFitRef.current = false;
  }, []);

  /* =======================================================
     DESTROY GOOGLE MAP
  ======================================================= */
  const destroyGoogleMap = useCallback(() => {
    if (googleClickListenerRef.current) {
      googleClickListenerRef.current.remove();
      googleClickListenerRef.current = null;
    }

    if (googleFromMarkerRef.current) {
      googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = null;
    }

    if (googleToMarkerRef.current) {
      googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = null;
    }

    googleCheckpointMarkersRef.current.forEach((marker) => {
      if (marker) marker.setMap(null);
    });
    googleCheckpointMarkersRef.current = [];

    googleMapInstanceRef.current = null;
    googleGeocoderRef.current = null;
    hasAutoFitRef.current = false;
  }, []);

  /* =======================================================
     INITIALIZE OPENSTREETMAP (LEAFLET)
  ======================================================= */
  useEffect(() => {
    if (loading || isGoogleMap) return undefined;
    if (!mapRef.current || mapInstanceRef.current) return undefined;

    destroyGoogleMap();

    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl:
        "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    const map = L.map(mapRef.current, { zoomControl: true }).setView(
      [20.5937, 78.9629],
      5
    );

    mapInstanceRef.current = map;

    L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
      {
        maxZoom: 20,
        subdomains: "abcd",
        attribution: "&copy; OpenStreetMap &copy; CARTO",
        crossOrigin: true,
      }
    ).addTo(map);

    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: false,
      position: "topleft",
      placeholder: "Search location...",
      errorMessage: "Location not found",
    })
      .on("markgeocode", (e) => {
        const center = e.geocode.center;

        if (e.geocode.bbox) {
          map.fitBounds(e.geocode.bbox);
        } else {
          map.setView(center, 16);
        }

        setLatLngOnActiveTarget(
          center.lat,
          center.lng,
          false,
          e.geocode.name || ""
        );
        toast.success(
          `Location found: ${e.geocode.name || "Selected location"}`
        );
      })
      .addTo(map);

    geocoderRef.current = geocoder;

    const handleMapClick = (ev) => {
      setLatLngOnActiveTarget(ev.latlng.lat, ev.latlng.lng, true);
    };

    map.on("click", handleMapClick);
    leafletClickHandlerRef.current = handleMapClick;

    setTimeout(() => map.invalidateSize(), 100);
    setTimeout(() => map.invalidateSize(), 500);
    setTimeout(() => map.invalidateSize(), 1000);

    syncMarkers(true);

    return () => {
      destroyLeafletMap();
    };
  }, [
    loading,
    isGoogleMap,
    setLatLngOnActiveTarget,
    destroyGoogleMap,
    destroyLeafletMap,
  ]);

  /* =======================================================
     INITIALIZE GOOGLE MAP
  ======================================================= */
  useEffect(() => {
    if (loading || !isGoogleMap) return undefined;
    if (!googleMapElementRef.current || googleMapInstanceRef.current)
      return undefined;

    let cancelled = false;

    setMapError("");
    setMapLoading(true);

    destroyLeafletMap();

    const initializeGoogleMap = async () => {
      try {
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);

        if (cancelled || !googleMapElementRef.current) return;

        const map = new window.google.maps.Map(googleMapElementRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          clickableIcons: false,
        });

        googleMapInstanceRef.current = map;
        googleGeocoderRef.current = new window.google.maps.Geocoder();

        googleClickListenerRef.current = map.addListener("click", (event) => {
          const lat = event.latLng?.lat();
          const lng = event.latLng?.lng();

          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setLatLngOnActiveTarget(lat, lng, true);
          }
        });

        syncMarkers(true);
      } catch (error) {
        console.error("Google Maps initialization error:", error);
        const message = getGoogleMapsErrorMessage(error);
        setMapError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    };

    initializeGoogleMap();

    return () => {
      cancelled = true;
      destroyGoogleMap();
    };
  }, [
    loading,
    isGoogleMap,
    setLatLngOnActiveTarget,
    destroyLeafletMap,
    destroyGoogleMap,
  ]);

  useEffect(() => {
    if (loading) return;

    const activeInstance = isGoogleMap
      ? googleMapInstanceRef.current
      : mapInstanceRef.current;

    if (!activeInstance) return;

    syncMarkers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isGoogleMap, form, checkpoints]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);
      errorShownRef.current = false;

      const requests = [
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(`${API_BASE_URL}/sentinel_route/${editId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          })
        );
      }

      const [dashboardRes, routeRes] = await Promise.all(requests);

      if (!dashboardRes.ok) {
        throw new Error("Failed to fetch dashboard");
      }

      const dashboardResult = await dashboardRes.json();
      const apiData = dashboardResult?.data || {};
      const companyDetails = apiData?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(
          companyDetails.admin_active ?? local.admin_active ?? 0
        ),
        company_address:
          companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      if (editId) {
        if (!routeRes?.ok) {
          throw new Error("Failed to fetch sentinel route details");
        }

        const routeResult = await routeRes.json();
        const route = routeResult?.data?.route || routeResult?.data || {};
        const routeCheckpoints =
          routeResult?.data?.checkpoints || route?.checkpoints || [];

        setForm({
          route_id:
            route.route_id ||
            route.sentinel_route_id ||
            route.id ||
            route.SlNo ||
            editId,
          r_name: route.r_name || route.route_name || "",
          r_from: route.r_from || route.from_name || route.from_location || "",
          r_to: route.r_to || route.to_name || route.to_location || "",
          r_from_lat: valueOrEmpty(route.r_from_lat || route.from_lat),
          r_from_lng: valueOrEmpty(route.r_from_lng || route.from_lng),
          r_to_lat: valueOrEmpty(route.r_to_lat || route.to_lat),
          r_to_lng: valueOrEmpty(route.r_to_lng || route.to_lng),
        });

        setCheckpoints(
          Array.isArray(routeCheckpoints) && routeCheckpoints.length > 0
            ? routeCheckpoints.map((item) => ({
                checkpoints_id: item.checkpoints_id || item.id || "",
                checkpoint_name:
                  item.checkpoint_name || item.name || item.cp_name || "",
                lat: valueOrEmpty(item.lat || item.latitude),
                lng: valueOrEmpty(item.lng || item.longitude),
                nfc_uid: valueOrEmpty(item.nfc_uid),
                nfc_token: valueOrEmpty(item.nfc_token),
                nfc_is_active:
                  item.nfc_is_active === undefined ||
                  item.nfc_is_active === null
                    ? true
                    : Boolean(item.nfc_is_active),
                verification_method:
                  item.verification_method || "NFC_OR_QR_GPS",
              }))
            : []
        );
      }
    } catch (error) {
      console.error("Sentinel route master page error:", error);
      toast.error(error.message || "Failed to load page data.");
      errorShownRef.current = true;
    } finally {
      setLoading(false);
    }
  };

  const triggerFenceSync = async (routeId, token) => {
    try {
      await fetch(`${API_BASE_URL}/sentinel_route/${routeId}/fence-sync`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.warn("Sentinel fence sync failed:", error);
    }
  };

  /* -------------------------------------------------------
     SYNC MARKERS
  ------------------------------------------------------- */
  const syncMarkers = (forceFit = false) => {
    if (isGoogleMap) {
      syncGoogleMarkers(forceFit);
    } else {
      syncLeafletMarkers(forceFit);
    }
  };

  const syncLeafletMarkers = (forceFit = false) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const fLat = parseFloat(form.r_from_lat || "");
    const fLng = parseFloat(form.r_from_lng || "");

    if (!Number.isNaN(fLat) && !Number.isNaN(fLng)) {
      if (fromMarkerRef.current) {
        fromMarkerRef.current.setLatLng([fLat, fLng]);
        fromMarkerRef.current.setPopupContent(
          `From: ${form.r_from || "Selected location"}`
        );
      } else {
        fromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
          .addTo(map)
          .bindPopup(`From: ${form.r_from || "Selected location"}`);
      }
    } else if (fromMarkerRef.current) {
      map.removeLayer(fromMarkerRef.current);
      fromMarkerRef.current = null;
    }

    const tLat = parseFloat(form.r_to_lat || "");
    const tLng = parseFloat(form.r_to_lng || "");

    if (!Number.isNaN(tLat) && !Number.isNaN(tLng)) {
      if (toMarkerRef.current) {
        toMarkerRef.current.setLatLng([tLat, tLng]);
        toMarkerRef.current.setPopupContent(
          `To: ${form.r_to || "Selected location"}`
        );
      } else {
        toMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
          .addTo(map)
          .bindPopup(`To: ${form.r_to || "Selected location"}`);
      }
    } else if (toMarkerRef.current) {
      map.removeLayer(toMarkerRef.current);
      toMarkerRef.current = null;
    }

    checkpointMarkersRef.current.forEach((marker) => {
      if (marker) map.removeLayer(marker);
    });

    checkpointMarkersRef.current = checkpoints.map((checkpoint, index) => {
      const lat = parseFloat(checkpoint.lat || "");
      const lng = parseFloat(checkpoint.lng || "");

      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

      return L.marker([lat, lng], { icon: checkpointIcon(index) })
        .addTo(map)
        .bindPopup(
          `Checkpoint ${index + 1}: ${
            checkpoint.checkpoint_name || "Selected location"
          }`
        );
    });

    const layers = [
      fromMarkerRef.current,
      toMarkerRef.current,
      ...checkpointMarkersRef.current,
    ].filter(Boolean);

    if (layers.length > 0 && (forceFit || !hasAutoFitRef.current)) {
      const group = L.featureGroup(layers);
      map.fitBounds(group.getBounds().pad(0.25));
      hasAutoFitRef.current = true;
    }
  };

  const syncGoogleMarkers = async (forceFit = false) => {
    const map = googleMapInstanceRef.current;
    if (!map || !window.google?.maps) return;

    // ---------- FROM (green + logo) ----------
    const fLat = parseFloat(form.r_from_lat || "");
    const fLng = parseFloat(form.r_from_lng || "");

    if (!Number.isNaN(fLat) && !Number.isNaN(fLng)) {
      const position = { lat: fLat, lng: fLng };
      const fromIconObj = await createGoogleLogoIcon("#16a34a", logo);

      if (googleFromMarkerRef.current) {
        googleFromMarkerRef.current.setPosition(position);
        googleFromMarkerRef.current.setIcon(fromIconObj);
        googleFromMarkerRef.current.setTitle(
          `From: ${form.r_from || "Selected location"}`
        );
      } else {
        googleFromMarkerRef.current = new window.google.maps.Marker({
          map,
          position,
          icon: fromIconObj,
          title: `From: ${form.r_from || "Selected location"}`,
          zIndex: 30,
        });
      }
    } else if (googleFromMarkerRef.current) {
      googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = null;
    }

    // ---------- TO (red + logo) ----------
    const tLat = parseFloat(form.r_to_lat || "");
    const tLng = parseFloat(form.r_to_lng || "");

    if (!Number.isNaN(tLat) && !Number.isNaN(tLng)) {
      const position = { lat: tLat, lng: tLng };
      const toIconObj = await createGoogleLogoIcon("#dc2626", logo);

      if (googleToMarkerRef.current) {
        googleToMarkerRef.current.setPosition(position);
        googleToMarkerRef.current.setIcon(toIconObj);
        googleToMarkerRef.current.setTitle(
          `To: ${form.r_to || "Selected location"}`
        );
      } else {
        googleToMarkerRef.current = new window.google.maps.Marker({
          map,
          position,
          icon: toIconObj,
          title: `To: ${form.r_to || "Selected location"}`,
          zIndex: 30,
        });
      }
    } else if (googleToMarkerRef.current) {
      googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = null;
    }

    // ---------- CHECKPOINTS (blue numbered pins) ----------
    googleCheckpointMarkersRef.current.forEach((marker) => {
      if (marker) marker.setMap(null);
    });

    googleCheckpointMarkersRef.current = checkpoints
      .map((checkpoint, index) => {
        const lat = parseFloat(checkpoint.lat || "");
        const lng = parseFloat(checkpoint.lng || "");

        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

        const icon = createGoogleCheckpointIcon(index + 1);

        return new window.google.maps.Marker({
          map,
          position: { lat, lng },
          icon,
          title: `Checkpoint ${index + 1}: ${
            checkpoint.checkpoint_name || "Selected location"
          }`,
          zIndex: 20,
        });
      })
      .filter(Boolean);

    // Fit bounds
    const activeMarkers = [
      googleFromMarkerRef.current,
      googleToMarkerRef.current,
      ...googleCheckpointMarkersRef.current,
    ].filter(Boolean);

    if (activeMarkers.length > 0 && (forceFit || !hasAutoFitRef.current)) {
      const bounds = new window.google.maps.LatLngBounds();
      activeMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
      map.fitBounds(bounds, 60);
      hasAutoFitRef.current = true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckpointChange = (index, field, value) => {
    setCheckpoints((prev) =>
      prev.map((checkpoint, i) =>
        i === index ? { ...checkpoint, [field]: value } : checkpoint
      )
    );
  };

  const addCheckpoint = () => {
    setCheckpoints((prev) => [...prev, { ...initialCheckpoint }]);
    toast.success("New checkpoint added!");
  };

  const removeCheckpoint = (index) => {
    setCheckpoints((prev) => prev.filter((_, i) => i !== index));
    toast.info("Checkpoint removed.");
  };

  const cancelNfcScan = useCallback(() => {
    if (nfcAbortControllerRef.current) {
      nfcAbortControllerRef.current.abort();
      nfcAbortControllerRef.current = null;
    }

    setScanningCheckpointIndex(null);
  }, []);

  useEffect(() => {
    return () => {
      if (nfcAbortControllerRef.current) {
        nfcAbortControllerRef.current.abort();
      }
    };
  }, []);

  const scanCheckpointNfc = async (checkpointIndex) => {
    if (!window.isSecureContext) {
      toast.error("NFC scanning requires HTTPS.");
      return;
    }

    if (!("NDEFReader" in window)) {
      toast.error(
        "Web NFC is not supported. Open the installed PWA in Chrome on an NFC-enabled Android phone."
      );
      return;
    }

    try {
      cancelNfcScan();

      const abortController = new AbortController();
      nfcAbortControllerRef.current = abortController;
      setScanningCheckpointIndex(checkpointIndex);

      const ndef = new window.NDEFReader();

      ndef.addEventListener(
        "readingerror",
        () => {
          toast.error("NFC tag detected, but the tag could not be read.");
          cancelNfcScan();
        },
        { signal: abortController.signal }
      );

      ndef.addEventListener(
        "reading",
        (event) => {
          const serialNumber = normalizeNfcUid(event.serialNumber || "");
          let sentinelToken = "";

          for (const record of event.message?.records || []) {
            if (record.recordType !== "text") continue;

            const value = readNdefTextRecord(record);

            if (value.startsWith("EBD:NFC:")) {
              sentinelToken = value;
              break;
            }
          }

          if (!serialNumber && !sentinelToken) {
            toast.error(
              "Tag detected, but no NFC serial number or Sentinel token was available."
            );
            cancelNfcScan();
            return;
          }

          setCheckpoints((previous) =>
            previous.map((checkpoint, index) =>
              index === checkpointIndex
                ? {
                    ...checkpoint,
                    nfc_uid: serialNumber || checkpoint.nfc_uid || "",
                    nfc_token: sentinelToken || checkpoint.nfc_token || "",
                    nfc_is_active: true,
                  }
                : checkpoint
            )
          );

          toast.success("NFC tag assigned to this checkpoint.");
          cancelNfcScan();
        },
        { signal: abortController.signal }
      );

      toast("Place the back of the phone near the NFC tag.", {
        icon: "📶",
        duration: 6000,
      });

      await ndef.scan({ signal: abortController.signal });
    } catch (error) {
      console.error("Web NFC scan error:", error);
      setScanningCheckpointIndex(null);
      nfcAbortControllerRef.current = null;

      if (error?.name === "AbortError") return;

      if (error?.name === "NotAllowedError") {
        toast.error("NFC permission was denied.");
      } else if (error?.name === "NotSupportedError") {
        toast.error("This phone, browser, or NFC tag is not supported.");
      } else if (error?.name === "InvalidStateError") {
        toast.error("Please enable NFC on the phone and try again.");
      } else {
        toast.error(error?.message || "Failed to scan the NFC tag.");
      }
    }
  };

  const removeCheckpointNfc = (checkpointIndex) => {
    setCheckpoints((previous) =>
      previous.map((checkpoint, index) =>
        index === checkpointIndex
          ? {
              ...checkpoint,
              nfc_uid: "",
              nfc_token: "",
              nfc_is_active: false,
            }
          : checkpoint
      )
    );

    toast.info("NFC assignment removed from this checkpoint.");
  };

  /* -------------------------------------------------------
     GEOCODE ADDRESS
  ------------------------------------------------------- */
  const geocodeAddress = (target, text) => {
    const query = String(text || "").trim();

    if (!query) {
      toast.error("Enter a location name first.");
      return;
    }

    setActiveTarget(target);
    activeTargetRef.current = target;

    if (isGoogleMap) {
      geocodeAddressGoogle(target, query);
      return;
    }

    geocodeAddressNominatim(target, query);
  };

  const applyGeocodeResult = (target, lat, lng, label) => {
    const lat7 = Number(lat).toFixed(7);
    const lng7 = Number(lng).toFixed(7);

    if (target === "from") {
      setForm((prev) => ({
        ...prev,
        r_from: label,
        r_from_lat: lat7,
        r_from_lng: lng7,
      }));
    } else if (target === "to") {
      setForm((prev) => ({
        ...prev,
        r_to: label,
        r_to_lat: lat7,
        r_to_lng: lng7,
      }));
    } else if (typeof target === "object" && target.type === "checkpoint") {
      setCheckpoints((prev) =>
        prev.map((checkpoint, index) =>
          index === target.index
            ? {
                ...checkpoint,
                checkpoint_name: label,
                lat: lat7,
                lng: lng7,
              }
            : checkpoint
        )
      );
    }

    toast.success(`Location "${label}" found and pinned!`);
  };

  const geocodeAddressNominatim = (target, query) => {
    fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
        query
      )}`
    )
      .then((res) => res.json())
      .then((results) => {
        if (!Array.isArray(results) || !results[0]) {
          toast.error(
            "Location not found. Please try a different search term."
          );
          return;
        }

        const item = results[0];
        const lat = Number(item.lat);
        const lng = Number(item.lon);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          toast.error("Invalid location coordinates.");
          return;
        }

        const label = item.display_name || query;

        applyGeocodeResult(target, lat, lng, label);

        if (mapInstanceRef.current) {
          mapInstanceRef.current.setView([lat, lng], 16);

          const tempMarker = L.marker([lat, lng])
            .addTo(mapInstanceRef.current)
            .bindPopup(`📍 ${label}`)
            .openPopup();

          setTimeout(() => {
            if (mapInstanceRef.current) {
              mapInstanceRef.current.removeLayer(tempMarker);
            }
          }, 3000);
        }
      })
      .catch((error) => {
        console.error("Search location error:", error);
        toast.error("Search location failed. Please try again.");
      });
  };

  const geocodeAddressGoogle = async (target, query) => {
    try {
      if (!googleGeocoderRef.current) {
        throw new Error("Google Maps is still loading.");
      }

      const response = await googleGeocoderRef.current.geocode({
        address: query,
      });

      const firstResult = response?.results?.[0];
      const position = firstResult?.geometry?.location;

      if (!position) {
        throw new Error("Location not found.");
      }

      const lat = position.lat();
      const lng = position.lng();
      const label = firstResult?.formatted_address || query;

      applyGeocodeResult(target, lat, lng, label);

      if (googleMapInstanceRef.current) {
        googleMapInstanceRef.current.panTo({ lat, lng });
        googleMapInstanceRef.current.setZoom(SELECTED_LOCATION_ZOOM);
      }
    } catch (error) {
      console.error("Google search location error:", error);
      toast.error(
        error.message || "Search location failed. Please try again."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.r_name.trim() || !form.r_from.trim() || !form.r_to.trim()) {
      toast.error("Route Name, From, and To are required.");
      return;
    }

    try {
      setSaving(true);
      errorShownRef.current = false;

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      const userData = JSON.parse(localStorage.getItem("userData") || "{}");

      const clientId =
        userData.admin_id ||
        userData.client_id ||
        userData.id ||
        localStorage.getItem("admin_id") ||
        localStorage.getItem("client_id") ||
        null;

      const payload = {
        r_name: form.r_name.trim(),
        r_from: form.r_from.trim(),
        r_to: form.r_to.trim(),
        r_from_lat: form.r_from_lat || null,
        r_from_lng: form.r_from_lng || null,
        r_to_lat: form.r_to_lat || null,
        r_to_lng: form.r_to_lng || null,
        client_id: clientId,
        checkpoints: checkpoints
          .map((checkpoint) => ({
            checkpoints_id: checkpoint.checkpoints_id || null,
            checkpoint_name: checkpoint.checkpoint_name.trim(),
            lat: checkpoint.lat || null,
            lng: checkpoint.lng || null,
            nfc_uid: checkpoint.nfc_uid || null,
            nfc_token: checkpoint.nfc_token || null,
            nfc_is_active: checkpoint.nfc_is_active !== false,
            verification_method:
              checkpoint.verification_method || "NFC_OR_QR_GPS",
          }))
          .filter((checkpoint) => checkpoint.checkpoint_name),
      };

      const url = isEdit
        ? `${API_BASE_URL}/sentinel_route/${form.route_id || editId}`
        : `${API_BASE_URL}/sentinel_route`;

      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save sentinel route");
      }

      const savedRouteId =
        data?.data?.route_id ||
        data?.data?.route?.route_id ||
        data?.data?.sentinel_route_id ||
        data?.data?.id ||
        form.route_id ||
        editId;

      if (savedRouteId) {
        await triggerFenceSync(savedRouteId, token);
      }

      if (isEdit) {
        toast.success(
          data.message ||
            "Sentinel route and checkpoints updated successfully!"
        );
      } else {
        toast.success("Sentinel route and checkpoints saved successfully!");
        setTimeout(() => navigate("/sentinel_route_list"), 1000);
      }
    } catch (error) {
      console.error("Save sentinel route error:", error);
      toast.error(error.message || "Failed to save sentinel route.");
      errorShownRef.current = true;
    } finally {
      setSaving(false);
    }
  };

  const routePage = "/sentinel_add_route";

  const stopKeyPropagation = (e) => {
    e.stopPropagation();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel route management...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="sentinel-add-route"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Route Management
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit
            ? "Edit an existing sentinel patrol route"
            : "Create a new sentinel patrol route"}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit ? "Edit Sentinel Route" : "New Sentinel Route"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage sentinel route name, start and end points, and
                checkpoint coordinates
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/sentinel_route_list")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Sentinel Route List
            </button>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              {isEdit ? "Edit Sentinel Route" : "New Sentinel Route"}
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Create sentinel patrol routes, assign checkpoints, and pick
              coordinates directly from the map
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Route Name{" "}
                  <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  name="r_name"
                  value={form.r_name}
                  onChange={handleChange}
                  onKeyDown={stopKeyPropagation}
                  placeholder="Enter route name"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* FROM */}
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  From <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="r_from"
                    value={form.r_from}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("from", form.r_from);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.r_from_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.r_from_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTarget("from");
                        activeTargetRef.current = "from";
                        toast("Click on the map to set: From location", {
                          icon: "📍",
                          duration: 3000,
                        });
                      }}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                      }}
                    >
                      Pick on Map
                    </button>

                    <button
                      type="button"
                      onClick={() => geocodeAddress("from", form.r_from)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>

              {/* TO */}
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  To <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="r_to"
                    value={form.r_to}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("to", form.r_to);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.r_to_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.r_to_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTarget("to");
                        activeTargetRef.current = "to";
                        toast("Click on the map to set: To location", {
                          icon: "📍",
                          duration: 3000,
                        });
                      }}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                      }}
                    >
                      Pick on Map
                    </button>

                    <button
                      type="button"
                      onClick={() => geocodeAddress("to", form.r_to)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* CHECKPOINTS */}
            <div className="mt-6">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-[16px] font-extrabold text-[var(--text-primary)] m-0">
                    Checkpoint Details
                  </h3>
                  <p className="mt-1 text-[12px] text-[var(--text-muted)]">
                    Checkpoints are optional. Add them only if this patrol
                    route needs intermediate verification points.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addCheckpoint}
                  className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                  style={{
                    background: "var(--btn-bg)",
                    color: "var(--btn-text)",
                    boxShadow: "0 4px 18px var(--accent-glow)",
                  }}
                >
                  + Add Checkpoint
                </button>
              </div>

              {checkpoints.length === 0 && (
                <div className="rounded-2xl border border-dashed border-[var(--surface-border)] bg-[var(--surface)] px-5 py-8 text-center">
                  <p className="m-0 text-[13px] font-semibold text-[var(--text-muted)]">
                    No checkpoints added. Click “+ Add Checkpoint” if you need
                    intermediate points on this route.
                  </p>
                </div>
              )}

              <div className="grid gap-4">
                {checkpoints.map((checkpoint, index) => (
                  <div
                    key={`${checkpoint.checkpoints_id || "new"}-${index}`}
                    className="rounded-2xl border border-[var(--surface-border)] bg-[var(--surface)] p-5"
                  >
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <span className="inline-flex px-3 py-1.5 rounded-full text-[11px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                        Checkpoint {index + 1}
                      </span>

                      <button
                        type="button"
                        onClick={() => removeCheckpoint(index)}
                        className="border-0 rounded-xl px-3.5 py-1.5 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                        style={{
                          background: "var(--red)",
                          color: "white",
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Checkpoint Name{" "}
                          <span className="text-[var(--red)] ml-0.5">*</span>
                        </label>
                        <input
                          type="text"
                          value={checkpoint.checkpoint_name}
                          onChange={(e) =>
                            handleCheckpointChange(
                              index,
                              "checkpoint_name",
                              e.target.value
                            )
                          }
                          onKeyDown={stopKeyPropagation}
                          placeholder="Checkpoint name"
                          required
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--card-bg)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Latitude
                        </label>
                        <input
                          type="text"
                          value={checkpoint.lat}
                          readOnly
                          placeholder="Lat"
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Longitude
                        </label>
                        <input
                          type="text"
                          value={checkpoint.lng}
                          readOnly
                          placeholder="Lng"
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          const target = { type: "checkpoint", index };
                          setActiveTarget(target);
                          activeTargetRef.current = target;
                          toast(
                            `Click on the map to set: Checkpoint ${index + 1}`,
                            {
                              icon: "📍",
                              duration: 3000,
                            }
                          );
                        }}
                        className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                        style={{
                          background: "var(--btn-bg)",
                          color: "var(--btn-text)",
                        }}
                      >
                        Pick on Map
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          geocodeAddress(
                            { type: "checkpoint", index },
                            checkpoint.checkpoint_name ||
                              `Checkpoint ${index + 1}`
                          )
                        }
                        className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                      >
                        Search Location
                      </button>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          Verification Method
                        </label>

                        <select
                          value={
                            checkpoint.verification_method || "NFC_OR_QR_GPS"
                          }
                          onChange={(e) =>
                            handleCheckpointChange(
                              index,
                              "verification_method",
                              e.target.value
                            )
                          }
                          className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--card-bg)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                        >
                          <option value="NFC_OR_QR_GPS">
                            NFC or QR + GPS
                          </option>
                          <option value="NFC_GPS">NFC + GPS</option>
                          <option value="QR_GPS">QR + GPS</option>
                        </select>

                        <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                          GPS validation remains enabled for NFC and QR scans.
                        </p>
                      </div>

                      <div>
                        <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                          NFC Tag
                        </label>

                        <div className="flex flex-col gap-2 sm:flex-row">
                          <input
                            type="text"
                            value={
                              checkpoint.nfc_token || checkpoint.nfc_uid || ""
                            }
                            readOnly
                            placeholder="No NFC tag assigned"
                            className="min-w-0 flex-1 rounded-xl border border-[var(--surface-border)] bg-[var(--card-bg)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-80"
                          />

                          <button
                            type="button"
                            onClick={() => scanCheckpointNfc(index)}
                            disabled={scanningCheckpointIndex !== null}
                            className="rounded-xl border-0 px-4 py-3 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              background: "var(--btn-bg)",
                              color: "var(--btn-text)",
                            }}
                          >
                            {scanningCheckpointIndex === index
                              ? "Scanning..."
                              : checkpoint.nfc_uid || checkpoint.nfc_token
                                ? "Replace NFC"
                                : "Scan NFC"}
                          </button>

                          {scanningCheckpointIndex === index && (
                            <button
                              type="button"
                              onClick={cancelNfcScan}
                              className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] px-4 py-3 text-[12px] font-extrabold text-[var(--text-primary)]"
                            >
                              Cancel
                            </button>
                          )}
                        </div>

                        {(checkpoint.nfc_uid || checkpoint.nfc_token) && (
                          <div className="mt-3 rounded-xl border border-[var(--accent-border)] bg-[var(--accent-faint)] p-3">
                            {checkpoint.nfc_uid && (
                              <p className="m-0 break-all text-[11px] font-bold text-[var(--text-primary)]">
                                UID: {checkpoint.nfc_uid}
                              </p>
                            )}

                            {checkpoint.nfc_token && (
                              <p className="mt-1 mb-0 break-all text-[11px] font-bold text-[var(--text-primary)]">
                                Token: {checkpoint.nfc_token}
                              </p>
                            )}

                            <button
                              type="button"
                              onClick={() => removeCheckpointNfc(index)}
                              className="mt-3 border-0 bg-transparent p-0 text-[11px] font-extrabold text-[var(--red)] cursor-pointer"
                            >
                              Remove NFC assignment
                            </button>
                          </div>
                        )}

                        <p className="mt-2 text-[11px] text-[var(--text-muted)]">
                          Use the installed PWA in Chrome on an NFC-enabled
                          Android phone over HTTPS.
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <button
                type="submit"
                disabled={saving}
                className="w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {saving
                  ? "Saving..."
                  : isEdit
                    ? "Update Sentinel Route"
                    : "Save Sentinel Route"}
              </button>
            </div>
          </form>
        </div>

        {/* Map Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Sentinel Route Map
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Search, click, and confirm exact map coordinates for the
                sentinel route and checkpoints
              </p>
            </div>

            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[var(--surface-border)] bg-[var(--surface)] px-3 py-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  mapError
                    ? "bg-red-500"
                    : mapLoading
                      ? "animate-pulse bg-amber-500"
                      : "bg-emerald-500"
                }`}
              />
              <span className="text-[11px] font-extrabold text-[var(--text-primary)]">
                {mapError
                  ? "Map Error"
                  : mapLoading
                    ? "Loading Map"
                    : isGoogleMap
                      ? "Google Maps"
                      : "OpenStreetMap"}
              </span>
            </div>
          </div>

          <div className="relative h-[420px] w-full overflow-hidden rounded-2xl border border-[var(--surface-border)] bg-[var(--surface)]">
            {!isGoogleMap && (
              <div ref={mapRef} className="absolute inset-0 h-full w-full" />
            )}

            {isGoogleMap && (
              <div
                ref={googleMapElementRef}
                className="absolute inset-0 h-full w-full"
              />
            )}

            {mapLoading && (
              <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
                <p className="text-[13px] font-extrabold text-[var(--text-primary)]">
                  Loading Google Maps...
                </p>
              </div>
            )}

            {!mapLoading && mapError && (
              <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--page-bg)] p-5">
                <div className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-6 text-center shadow-xl">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-2xl">
                    ⚠️
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold text-red-700">
                    Map could not be loaded
                  </h3>
                  <p className="mt-2 text-sm font-semibold leading-6 text-red-600">
                    {mapError}
                  </p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="mt-5 inline-flex h-11 items-center justify-center rounded-xl bg-red-600 px-5 text-sm font-extrabold text-white transition hover:bg-red-700"
                  >
                    Reload Page
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-control-geocoder {
          border-radius: 12px !important;
          border: 1px solid var(--surface-border) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
          margin: 10px !important;
        }
        .leaflet-control-geocoder-form {
          display: flex !important;
          align-items: center !important;
        }
        .leaflet-control-geocoder input {
          background: var(--surface) !important;
          color: var(--text-primary) !important;
          padding: 8px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          outline: none !important;
          font-size: 13px !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          min-width: 200px !important;
        }
        .leaflet-control-geocoder input::placeholder {
          color: var(--text-faint) !important;
        }
        .leaflet-control-geocoder .leaflet-control-geocoder-icon {
          background: var(--btn-bg) !important;
          border-radius: 8px !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          margin-left: 4px !important;
          border: none !important;
        }
        .leaflet-control-geocoder .leaflet-control-geocoder-icon:before {
          content: "🔍" !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }
        .leaflet-popup-content-wrapper {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-radius: 12px !important;
          border: 1px solid var(--card-border) !important;
        }
        .leaflet-popup-tip {
          background: var(--card-bg) !important;
        }
        .leaflet-popup-content {
          color: var(--text-primary) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }
        .leaflet-control-zoom {
          margin: 10px !important;
        }
        .leaflet-control-zoom a {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-color: var(--surface-border) !important;
        }
        .leaflet-control-zoom a:hover {
          background: var(--accent-faint) !important;
        }
      `}</style>
    </Layout>
  );
}